import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { authApi } from '@core/api/auth.api';
import { roleLabel } from '@core/auth/roleLabel';
import { qk } from '@core/queries/keys';
import { useAuthStore } from '@core/stores/auth.store';
import { Icon } from '@ui/Icon';
import { IconButton } from '@ui/IconButton';
import { NotificationBell } from './NotificationBell';
import type { User } from '@core/types/auth';

/**
 * Top app bar. Ports `core/shared/header/`.
 *
 * DELETED, not ported:
 *   - The search box. `MatInputModule`/`MatFormFieldModule` were imported and
 *     `searchText` / `onSearchTextChange` / `clearSearch` existed, but no search input
 *     appears in the current template — all dead.
 *   - `updateBreadcrumb()`. It maintained `breadcrumbText` from a `router.url` switch
 *     covering `/simple-search`, `/custom-search`, `/other-search` and `/reports` —
 *     four routes that do not exist — and the result was never rendered.
 *   - `loadPermissions()`. The Angular header bootstrapped app-wide permissions, which
 *     is a surprising place for it and meant the call fired twice on every boot
 *     (AppComponent did it too). It now happens once, in `AppLayout`.
 */
function getUserDisplayName(user: User | null): string {
  // Angular fallback chain: email → user_email → 'User'.
  return user?.email ?? user?.user_email ?? 'User';
}

export function Header({
  onLogout,
  isLoggingOut,
  onOpenNav,
}: {
  onLogout: () => void;
  isLoggingOut: boolean;
  /** Phone only: opens the slide-over menu. The button is hidden at wider widths by CSS. */
  onOpenNav?: () => void;
}) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const displayName = getUserDisplayName(user);
  // Shares My Profile's query key, so the two never disagree and it is fetched once.
  const userInfo = useQuery({
    queryKey: qk.userInfo(),
    queryFn: () => authApi.userInfo(),
    staleTime: 5 * 60 * 1000,
  });
  const role = roleLabel(userInfo.data?.data?.role);

  return (
    <header className="vm-header">
      <div className="vm-header-left">
        {onOpenNav && (
          <IconButton
            label="Open menu"
            className="vm-mobile-nav-btn min-[769px]:hidden"
            onClick={onOpenNav}
            aria-controls="vm-main-nav"
          >
            <Icon className="text-[22px]">menu</Icon>
          </IconButton>
        )}
        <div className="vm-brand">
          <img
            src="/assets/verdantis-logo.svg"
            alt="Verdantis"
            width={164}
            height={38}
            className="block h-[38px] w-auto max-w-[164px] max-[768px]:h-[30px] max-[768px]:max-w-[128px]"
          />
          <span className="vm-brand-title">Integrity :: Business Partner</span>
        </div>
      </div>

      <div className="vm-header-right">
        <NotificationBell />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            {/*
              On a phone only the account icon shows (`max-[768px]:hidden`, as in shell.css); the
              email stays the button's name for screen readers, and the menu itself still shows it.
            */}
            <button type="button" className="vm-user-menu-btn" aria-label={displayName}>
              <Icon size="lg" className="mr-2 max-[768px]:mr-0">
                account_circle
              </Icon>
              <span className="mx-2 max-w-[220px] truncate text-xs max-[768px]:hidden">
                {displayName}
              </span>
              <Icon size="md" className="ml-1 max-[768px]:hidden">
                keyboard_arrow_down
              </Icon>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="vm-menu-panel z-[1200] w-[260px]"
            >
              <div className="vm-menu-header">
                <Icon className="text-[40px]">account_circle</Icon>
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="truncate text-[13px] font-semibold">{displayName}</span>
                  {/*
                    The user's real role, from `/management/userinfo` — the same source as My
                    Profile. It was a hardcoded "Administrator" (header.component.html:61), which
                    every user saw whatever they held. Hidden rather than guessed while loading or
                    when the user has no role.
                  */}
                  {role && (
                    <span className="truncate text-[11.5px] font-normal opacity-90">{role}</span>
                  )}
                </div>
              </div>

              <DropdownMenu.Item className="vm-menu-item" onSelect={() => navigate('/profile')}>
                <Icon size="md">person</Icon>
                <span>My Profile</span>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="bg-line h-px" />

              <DropdownMenu.Item
                className="vm-menu-item logout-item"
                disabled={isLoggingOut}
                onSelect={onLogout}
              >
                <Icon size="md">logout</Icon>
                <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
